"use client";

import { BlogCommentType, BlogCoverType } from "@v2/types/blog";
import { Box, Button, colors, FormControlLabel, IconButton, Modal, Switch, TextField, Typography, useTheme } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import api from "@v2/lib/api";
import axios from "@v2/lib/axios";
import { useAppSelector } from "@v2/lib/redux/hooks";
import { useSnackbar } from "notistack";
import { MessageSquarePlus, MessageSquareReply, X } from "lucide-react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { ChangeEvent, useCallback, useState } from "react";

type Props = {
    postSlug: string;
    postTitle?: string;
    postCover?: BlogCoverType | null;
    replyTo?: BlogCommentType;
    onSuccess?: () => void;
    variant?: "contained" | "text";
};

export default function BlogAddCommentButton({ postSlug, postTitle, postCover, replyTo, onSuccess, variant = "contained" }: Props) {
    const session = useAppSelector((s) => s.general.session);
    const isAuth = session?.isAuthenticated ?? false;
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const currentPath = pathname + (searchParams.size > 0 ? `?${searchParams.toString()}` : "");
    const signInHref = `/fa/sign-in?callback_url=${encodeURIComponent(`${currentPath}#comments`)}`;
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();

    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [isUnknown, setIsUnknown] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleOpen = useCallback(() => {
        if (!isAuth) {
            enqueueSnackbar({
                message: (
                    <Box>
                        <Typography sx={{ fontSize: 14 }}>برای ثبت نظر ابتدا وارد حساب کاربری شوید</Typography>
                        <Button color="inherit" variant="outlined" component={Link} prefetch={false} href={signInHref} sx={{ mt: 1, minWidth: 68 }}>ورود</Button>
                    </Box>
                ),
            });
            return;
        }
        setOpen(true);
    }, [isAuth, signInHref, enqueueSnackbar]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setText("");
        setIsUnknown(false);
    }, []);

    const handleSubmit = useCallback(() => {
        if (!text.trim()) return;
        setSubmitting(true);
        axios.post(api.blog_post_comments(postSlug), {
            description: text,
            is_unknown: isUnknown,
            comment_id: replyTo?.id ?? null,
        }).then(() => {
            setSubmitting(false);
            handleClose();
            enqueueSnackbar("نظر شما ثبت شد و پس از بررسی منتشر خواهد شد", { variant: "success", autoHideDuration: 6000 });
            onSuccess?.();
        }).catch((err) => {
            setSubmitting(false);
            enqueueSnackbar(err?.response?.data?.message ?? "خطا در ارسال نظر", { variant: "error" });
        });
    }, [text, isUnknown, replyTo, postSlug, handleClose, onSuccess, enqueueSnackbar]);

    const isReply = !!replyTo;

    return (
        <>
            {isReply ? (
                <Button
                    size="small"
                    color="inherit"
                    onClick={handleOpen}
                    startIcon={<MessageSquareReply size={14} />}
                    sx={{ fontSize: 12, color: colors.grey[500], minWidth: 0, px: 1, py: 0.5 }}
                >
                    پاسخ
                </Button>
            ) : (
                <Button
                    variant={variant === "contained" ? "contained" : "text"}
                    color="primary"
                    disableElevation
                    onClick={handleOpen}
                    startIcon={<MessageSquarePlus size={16} />}
                    sx={{ height: 36, borderRadius: 0.8, ...(variant === "text" && { color: "primary.main", px: 1 }) }}
                >
                    ثبت نظر
                </Button>
            )}

            <Modal open={open} onClose={handleClose}>
                <Box sx={{
                    bgcolor: "#fff",
                    maxWidth: 520,
                    margin: "auto",
                    mt: 8,
                    borderRadius: 2,
                    outline: "none",
                    [theme.breakpoints.down("sm")]: { position: "absolute", bottom: 0, left: 0, right: 0, mt: 0, borderRadius: "16px 16px 0 0" },
                }}>
                    <Box sx={{ position: "relative", borderBottom: "1px solid", borderColor: "divider", height: 52, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                            {isReply ? `پاسخ به نظر` : "ثبت نظر"}
                        </Typography>
                        <IconButton sx={{ position: "absolute", left: 12, width: 32, height: 32 }} onClick={handleClose}>
                            <X size={18} />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 2.5 }}>
                        {!isReply && postTitle && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, bgcolor: colors.grey[50], borderRadius: 1.5, p: 1.5, mb: 2.5 }}>
                                {postCover?.id && (
                                    <Box sx={{ width: 72, height: 48, borderRadius: 1, overflow: "hidden", flexShrink: 0, position: "relative" }}>
                                        <MediaV2
                                            media_id={String(postCover.id)}
                                            alt={postCover.alt ?? postTitle}
                                            width={72}
                                            height={48}
                                            imageProps={{ fill: true, style: { objectFit: "cover" } }}
                                            boxProps={{ sx: { width: "100%", height: "100%" } }}
                                        />
                                    </Box>
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 11, color: colors.grey[500], mb: 0.3 }}>نظر برای مقاله:</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.grey[800], lineHeight: 1.5 }} noWrap>
                                        {postTitle}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {isReply && (
                            <Box sx={{ bgcolor: colors.grey[50], borderRadius: 1, p: 1.5, mb: 2, borderRight: "3px solid", borderColor: "primary.main" }}>
                                <Typography sx={{ fontSize: 12, color: colors.grey[500], mb: 0.5 }}>در پاسخ به:</Typography>
                                <Typography sx={{ fontSize: 13, color: colors.grey[700], lineHeight: 1.7 }} noWrap>
                                    {replyTo.description.slice(0, 120)}{replyTo.description.length > 120 ? "..." : ""}
                                </Typography>
                            </Box>
                        )}

                        <TextField
                            autoFocus
                            multiline
                            rows={4}
                            fullWidth
                            placeholder="نظر خود را بنویسید ..."
                            value={text}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
                        />

                        <FormControlLabel
                            sx={{ mt: 1.5, flexDirection: "row-reverse", width: "100%", justifyContent: "space-between", m: 0, "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                            control={<Switch checked={isUnknown} onChange={(_, v) => setIsUnknown(v)} />}
                            label="ارسال به صورت ناشناس"
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            disableElevation
                            disabled={!text.trim() || text.trim().length < 5}
                            loading={submitting}
                            onClick={handleSubmit}
                            sx={{ height: 48, mt: 2 }}
                        >
                            ارسال نظر
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}

